import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  conversationsApi,
  type StaffConsultation,
  type StaffConsultationAnswerRequest,
  type StaffConsultationAnswerResponse,
} from '@/services/conversationsApi'

/** Cuelga de la clave de la conversación: cualquier refresco de la conversación también refresca la duda. */
const staffConsultationKey = (conversationId: string) => ['conversation', conversationId, 'staff-consultation'] as const

function friendlyError(error: unknown): string {
  const response = (error as { response?: { status?: number; data?: { error?: string } } } | null)?.response
  if (response?.status === 409) return 'Otra persona del equipo ya resolvió esta duda.'
  return response?.data?.error || 'No se pudo completar la acción. Inténtalo de nuevo.'
}

export interface UseStaffConsultation {
  consultation: StaffConsultation | null | undefined
  isLoading: boolean
  loadError: boolean
  reload: () => void
  answer: (payload: StaffConsultationAnswerRequest) => Promise<StaffConsultationAnswerResponse>
  takeOver: () => Promise<StaffConsultation>
  isAnswering: boolean
  isTakingOver: boolean
  actionError: string | null
  resetActionError: () => void
}

export function useStaffConsultation(conversationId: string | undefined, enabled: boolean): UseStaffConsultation {
  const queryClient = useQueryClient()
  const id = conversationId ?? ''

  const query = useQuery({
    queryKey: staffConsultationKey(id),
    queryFn: () => conversationsApi.getStaffConsultation(id),
    enabled: enabled && id.length > 0,
    refetchInterval: 15000,
  })

  const refreshConversation = () => {
    void queryClient.invalidateQueries({ queryKey: ['conversation', id] })
    void queryClient.invalidateQueries({ queryKey: ['conversations'] })
  }

  const answerMutation = useMutation({
    mutationFn: (payload: StaffConsultationAnswerRequest) => {
      const consultationId = query.data?.id
      if (!consultationId) return Promise.reject(new Error('No hay una duda pendiente.'))
      return conversationsApi.answerStaffConsultation(id, consultationId, payload)
    },
    onSuccess: refreshConversation,
    // Si otra persona ya la resolvió, la tarjeta debe desaparecer y no quedarse con datos viejos.
    onError: () => void queryClient.invalidateQueries({ queryKey: staffConsultationKey(id) }),
  })

  const takeOverMutation = useMutation({
    mutationFn: () => {
      const consultationId = query.data?.id
      if (!consultationId) return Promise.reject(new Error('No hay una duda pendiente.'))
      return conversationsApi.takeOverStaffConsultation(id, consultationId)
    },
    onSuccess: refreshConversation,
    onError: () => void queryClient.invalidateQueries({ queryKey: staffConsultationKey(id) }),
  })

  const actionError = answerMutation.error
    ? friendlyError(answerMutation.error)
    : takeOverMutation.error
      ? friendlyError(takeOverMutation.error)
      : null

  return {
    consultation: query.data,
    isLoading: query.isLoading,
    loadError: query.isError,
    reload: () => void query.refetch(),
    answer: answerMutation.mutateAsync,
    takeOver: takeOverMutation.mutateAsync,
    isAnswering: answerMutation.isPending,
    isTakingOver: takeOverMutation.isPending,
    actionError,
    resetActionError: () => {
      answerMutation.reset()
      takeOverMutation.reset()
    },
  }
}
